import { zodResolver } from "@hookform/resolvers/zod";
import { router, usePage } from "@inertiajs/react";
import { Check, ChevronsUpDown, Info, TriangleAlert, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { AsyncSearchCombobox } from "@/components/admin-portal/patient/async-search-combobox";
import { ResponsiveDialogButton } from "@/components/shared/responsive-dialog";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@/components/ui/command";
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { deepTransformKeysToSnakeCase } from "@/hooks/use-change-case";
import { useIsMobile } from "@/hooks/use-mobile";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import {
	type OwnerChangeWarning,
	ownerChangeWarning,
} from "@/lib/vouchers/owner-change-warning";
import type { Package } from "@/types/admin-portal/package";
import type { Voucher } from "@/types/admin-portal/vouchers";
import type { GlobalPageProps, ResponsiveDialogMode } from "@/types/globals";

type VoucherFormValues = z.infer<typeof formSchema>;

const DISCOUNT_TYPE_OPTIONS = [
	{ value: "PERCENTAGE", label: "Percentage" },
	{ value: "FIXED", label: "Fixed Amount" },
] as const;

const OWNER_CHANGE_COPY: Record<OwnerChangeWarning, string> = {
	move: "Bonus dari order yang sudah memakai kode ini akan dipindahkan ke terapis baru. Saldo terapis lama bisa minus kalau bonusnya sudah dicairkan.",
	release:
		"Bonus dari order yang sudah memakai kode ini akan ditarik dari terapis lama. Saldonya bisa minus kalau bonusnya sudah dicairkan. Bonus dari order yang belum lunas ikut dibatalkan.",
};

type TherapistOption = {
	id: string;
	name: string;
	registrationNumber: string;
	employmentStatus: string;
};

const formSchema = z
	.object({
		code: z.string().min(3, { message: "Code must be at least 3 characters" }),
		name: z.string().refine(
			(value) => {
				const trimmed = value.trim();
				return trimmed.length === 0 || trimmed.length >= 3;
			},
			{ message: "Name must be at least 3 characters" },
		),
		description: z.string().optional(),
		discountType: z.enum(["PERCENTAGE", "FIXED", "PER_PACKAGE"]),
		discountValue: z.string(),
		maxDiscountAmount: z
			.string()
			.optional()
			.refine(
				(value) => {
					if (!value || value.trim() === "") return true;
					const parsed = Number(value);
					return !Number.isNaN(parsed) && parsed >= 0;
				},
				{ message: "Max discount amount must be zero or greater" },
			),
		minOrderAmount: z
			.string()
			.optional()
			.refine(
				(value) => {
					if (!value || value.trim() === "") return true;
					const parsed = Number(value);
					return !Number.isNaN(parsed) && parsed >= 0;
				},
				{
					message: "Min order amount must be zero or greater",
				},
			),
		quota: z.string().refine(
			(value) => {
				const parsed = Number(value);
				return !Number.isNaN(parsed) && Number.isInteger(parsed) && parsed > 0;
			},
			{ message: "Quota must be a positive whole number" },
		),
		validFrom: z
			.string()
			.optional()
			.refine(
				(value) => {
					if (!value || value.trim() === "") return true;
					return !Number.isNaN(Date.parse(value));
				},
				{ message: "Enter a valid start date" },
			),
		validUntil: z
			.string()
			.optional()
			.refine(
				(value) => {
					if (!value || value.trim() === "") return true;
					return !Number.isNaN(Date.parse(value));
				},
				{ message: "Enter a valid end date" },
			),
		isActive: z.boolean(),
		adminOnly: z.boolean(),
		packageIds: z
			.array(z.string())
			.min(1, { message: "Select at least one package" }),
		bonusRo: z.boolean(),
		therapistId: z.string(),
	})
	.refine((values) => !values.bonusRo || values.therapistId !== "", {
		message: "Select the owner therapist",
		path: ["therapistId"],
	})
	// A per-package voucher keeps 0: its discount comes from the Bonus RO package rules.
	.refine(
		(values) =>
			values.discountType === "PER_PACKAGE" || Number(values.discountValue) > 0,
		{
			message: "Discount value must be greater than 0",
			path: ["discountValue"],
		},
	);

type VoucherNewFormFieldsProps = {
	packages: Package[];
	forceMode?: ResponsiveDialogMode;
	handleOpenChange?: (value: boolean) => void;
	voucher?: Voucher | null;
	// False before backend migration 0111, or for an admin without the backend ADMIN role, which the owner endpoint requires.
	bonusRoEditable?: boolean;
};

export default function NewVoucherFormFields({
	packages,
	forceMode,
	handleOpenChange,
	voucher,
	bonusRoEditable = false,
}: VoucherNewFormFieldsProps) {
	const { props: globalProps } = usePage<
		GlobalPageProps & { errors?: Record<string, string[]> }
	>();

	const [isLoading, setIsLoading] = useState(false);
	const { toast } = useToast();
	const isMobile = useIsMobile();
	const isEditMode = Boolean(voucher);
	const buttonProps = useMemo(
		() => ({
			isLoading,
			forceMode,
			submitText: voucher ? "Update Voucher" : "Create Voucher",
		}),
		[isLoading, forceMode, voucher],
	);
	const packageGroups = useMemo(() => {
		const groups = new Map<
			string,
			{ key: string; label: string; items: Package[] }
		>();

		packages.forEach((pkg) => {
			const rawKey = pkg.service?.name ?? "Standalone Packages";
			const key = rawKey.toUpperCase();
			const label =
				pkg.service?.name?.replaceAll("_", " ") ?? "Standalone Packages";

			if (!groups.has(key)) {
				groups.set(key, { key, label, items: [] });
			}

			groups.get(key)?.items.push(pkg);
		});

		return Array.from(groups.values());
	}, [packages]);

	const form = useForm<VoucherFormValues>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			code: "",
			name: "",
			description: "",
			discountType: "FIXED",
			discountValue: "",
			maxDiscountAmount: "",
			minOrderAmount: "",
			quota: "",
			validFrom: "",
			validUntil: "",
			isActive: true,
			adminOnly: false,
			packageIds: [],
			bonusRo: false,
			therapistId: "",
		},
		mode: "onBlur",
	});

	// Name of the picked owner; the form itself only holds the id.
	const [ownerName, setOwnerName] = useState("");

	useEffect(() => {
		if (!voucher) return;

		form.reset(mapVoucherToFormValues(voucher));
		setOwnerName(ownerOf(voucher)?.name ?? "");
	}, [voucher, form]);

	const bonusRo = form.watch("bonusRo");
	// Its discount comes from the Bonus RO package rules; the server ignores type and value on its edit.
	const isBonusRoVoucher = isEditMode && voucher?.program === "BONUS_RO";
	// Unlinking a Bonus RO voucher leaves it FIXED 0 and inactive (backend#207): it needs a real discount again.
	const discountLocked =
		isEditMode && !isBonusRoVoucher && Number(voucher?.discountValue) > 0;

	// biome-ignore lint/correctness/useExhaustiveDependencies: voucher is a re-run trigger: the reset that follows the voucher load wipes server errors set before it
	useEffect(() => {
		if (!globalProps?.errors) return;

		for (const [field, messages] of Object.entries(globalProps.errors)) {
			if (field === "fullMessages") continue;
			form.setError(field as keyof VoucherFormValues, {
				type: "server",
				message: Array.isArray(messages) ? messages.join(", ") : messages,
			});
		}
	}, [globalProps?.errors, form, voucher]);

	const onSubmit = useCallback(
		(values: VoucherFormValues) => {
			const baseRoute =
				globalProps.adminPortal.router.adminPortal.vouchers.index;
			const routeURL = isEditMode ? `${baseRoute}/${voucher?.id}` : baseRoute;
			const payload = deepTransformKeysToSnakeCase({
				voucher: {
					code: values.code.trim().toUpperCase(),
					name: values.name.trim(),
					description: values.description?.trim() || null,
					discountType: values.discountType,
					discountValue: Number(values.discountValue), // Use as-is (user can enter K or full rupiah)
					maxDiscountAmount: parseOptionalNumber(values.maxDiscountAmount), // Use as-is
					minOrderAmount: parseOptionalNumber(values.minOrderAmount), // Use as-is
					quota: Number(values.quota),
					validFrom: values.validFrom
						? new Date(values.validFrom).toISOString()
						: null,
					validUntil: values.validUntil
						? new Date(values.validUntil).toISOString()
						: null,
					isActive: values.isActive,
					adminOnly: values.adminOnly,
					packageIds: values.packageIds.map((id) => Number(id)),
					// Sent only when the column exists; null unlinks the owner.
					...(bonusRoEditable && {
						therapistId: values.bonusRo ? values.therapistId : null,
					}),
				},
			});
			const requestOptions = {
				preserveScroll: true,
				preserveState: true,
				// selectedVoucher: a create whose owner link fails redirects to that voucher's
				// edit form, which needs the voucher this partial reload would otherwise skip.
				only: ["adminPortal", "flash", "errors", "vouchers", "selectedVoucher"],
				onStart: () => setIsLoading(true),
				onSuccess: () => {
					form.reset();
					handleOpenChange?.(false);
					toast({
						description: isEditMode
							? "Voucher updated successfully."
							: "Voucher created successfully.",
					});
				},
				onError: (errors: Record<string, unknown>) => {
					// A failed owner link already explains itself in the flash alert.
					if (errors.therapistId) return;
					toast({
						description: isEditMode
							? "Failed to update voucher. Please check the form."
							: "Failed to create voucher. Please check the form.",
						variant: "destructive",
					});
				},
				onFinish: () => setTimeout(() => setIsLoading(false), 300),
			};

			if (isEditMode) {
				router.put(routeURL, payload, requestOptions);
				return;
			}

			router.post(routeURL, payload, requestOptions);
		},
		[
			form.reset,
			globalProps.adminPortal.router.adminPortal.vouchers.index,
			isEditMode,
			voucher?.id,
			handleOpenChange,
			toast,
			bonusRoEditable,
		],
	);

	const [ownerChange, setOwnerChange] = useState<{
		warning: OwnerChangeWarning;
		values: VoucherFormValues;
	} | null>(null);
	// Separate from ownerChange so the copy stays put while the dialog animates out.
	const [isOwnerChangeOpen, setIsOwnerChangeOpen] = useState(false);

	const onValidSubmit = (values: VoucherFormValues) => {
		// Without Bonus RO support the payload never carries an owner, so nothing moves.
		const warning = bonusRoEditable
			? ownerChangeWarning(voucher, values)
			: null;
		if (warning) {
			setOwnerChange({ warning, values });
			setIsOwnerChangeOpen(true);
			return;
		}
		onSubmit(values);
	};

	return (
		<Form {...form}>
			<AlertDialog open={isOwnerChangeOpen} onOpenChange={setIsOwnerChangeOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{ownerChange?.warning === "release"
								? "Lepas pemilik voucher?"
								: "Ganti pemilik voucher?"}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{ownerChange && OWNER_CHANGE_COPY[ownerChange.warning]}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Batal</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => {
								if (ownerChange) onSubmit(ownerChange.values);
							}}
						>
							Lanjutkan
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<form onSubmit={form.handleSubmit(onValidSubmit)}>
				<div
					className={cn(
						"space-y-6 px-1",
						isMobile ? "max-h-[75dvh] overflow-y-auto pb-4" : "",
					)}
				>
					<FormField
						control={form.control}
						name="packageIds"
						render={({ field }) => {
							const selectedPackages = packages.filter((pkg) =>
								field.value.includes(String(pkg.id)),
							);

							const visiblePackages = selectedPackages.slice(0, 3);
							const buttonLabel =
								selectedPackages.length === 0
									? "Select packages..."
									: selectedPackages.length <= 3
										? visiblePackages.map((pkg) => pkg.name).join(", ")
										: `${visiblePackages
												.map((pkg) => pkg.name)
												.join(", ")} +${selectedPackages.length - 3} more`;

							return (
								<FormItem>
									<FormLabel>Packages</FormLabel>
									<Popover modal>
										<PopoverTrigger asChild>
											<FormControl>
												<Button
													type="button"
													variant="outline"
													className={cn(
														"w-full justify-between font-normal px-3 bg-input",
														!field.value.length && "text-muted-foreground",
													)}
												>
													<span className="truncate pr-4 text-left">
														{buttonLabel}
													</span>
													<div className="flex items-center gap-2">
														{field.value.length > 0 && (
															// biome-ignore lint/a11y/noStaticElementInteractions: act like a button
															<div
																onClick={(event) => {
																	event.preventDefault();
																	event.stopPropagation();
																	field.onChange([]);
																}}
																onKeyDown={() => {}}
																className="text-muted-foreground transition hover:text-foreground"
															>
																<X className="h-4 w-4" />
															</div>
														)}
														<ChevronsUpDown className="opacity-50" />
													</div>
												</Button>
											</FormControl>
										</PopoverTrigger>
										<PopoverContent
											align="start"
											sideOffset={8}
											className="p-0"
										>
											<Command>
												<CommandInput
													placeholder="Search packages..."
													className="h-10"
												/>
												<CommandList>
													<CommandEmpty>No packages found.</CommandEmpty>
													{packageGroups.length === 0 ? (
														<CommandGroup heading="Packages">
															<CommandItem value="" disabled>
																No packages available
															</CommandItem>
														</CommandGroup>
													) : (
														packageGroups.map(({ key, label, items }) => (
															<CommandGroup key={key} heading={label}>
																{items.map((pkg) => {
																	const value = String(pkg.id);
																	const isSelected =
																		field.value.includes(value);

																	return (
																		<CommandItem
																			key={value}
																			value={`${pkg.name} ${pkg.service?.name ?? ""}`}
																			onSelect={() => {
																				const next = isSelected
																					? field.value.filter(
																							(id) => id !== value,
																						)
																					: [...field.value, value];
																				field.onChange(next);
																			}}
																			className={cn(
																				"flex items-center justify-between",
																				isSelected &&
																					"bg-accent text-accent-foreground",
																			)}
																		>
																			<span>
																				{pkg.name} &bull; {pkg.numberOfVisit}{" "}
																				Visit(s)
																			</span>
																			<Check
																				className={cn(
																					"ml-auto h-4 w-4",
																					isSelected
																						? "opacity-100"
																						: "opacity-0",
																				)}
																			/>
																		</CommandItem>
																	);
																})}
															</CommandGroup>
														))
													)}
												</CommandList>
											</Command>
										</PopoverContent>
									</Popover>
									<FormMessage />
								</FormItem>
							);
						}}
					/>

					<div className="grid gap-4 md:grid-cols-2">
						<FormField
							control={form.control}
							name="code"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Code</FormLabel>
									<FormControl>
										<Input
											{...field}
											placeholder="Enter voucher code..."
											className="bg-input"
											disabled={isEditMode}
											onChange={(event) =>
												field.onChange(event.target.value.toUpperCase())
											}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="name"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Name</FormLabel>
									<FormControl>
										<Input
											{...field}
											placeholder="Enter voucher name..."
											className="bg-input"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						{isBonusRoVoucher ? (
							<p
								role="note"
								className="flex gap-2 rounded-md border p-3 text-sm text-muted-foreground md:col-span-2"
							>
								<Info className="mt-0.5 size-4 shrink-0" />
								Potongan per paket: nominalnya diatur di tab Rules halaman Bonus
								RO, jadi tipe dan nilai diskon voucher ini tidak bisa diubah.
							</p>
						) : (
							<>
								<FormField
									control={form.control}
									name="discountType"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Discount Type</FormLabel>
											<FormControl>
												<Select
													disabled={discountLocked}
													onValueChange={field.onChange}
													value={field.value}
												>
													<SelectTrigger className="bg-input">
														<SelectValue placeholder="Select discount type" />
													</SelectTrigger>
													<SelectContent>
														{DISCOUNT_TYPE_OPTIONS.map((option) => (
															<SelectItem
																key={option.value}
																value={option.value}
															>
																{option.label}
															</SelectItem>
														))}
													</SelectContent>
												</Select>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="discountValue"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Discount Value</FormLabel>
											<FormControl>
												<Input
													{...field}
													type="number"
													min={0}
													disabled={discountLocked}
													step="1"
													placeholder="e.g., 50000"
													className="bg-input appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
												/>
											</FormControl>
											<FormDescription>
												Enter the discount amount in rupiah (e.g., 50000 for
												Rp50.000)
											</FormDescription>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="maxDiscountAmount"
									render={({ field }) => (
										<FormItem>
											<FormLabel className="capitalize">
												Max Discount Amount{" "}
												<span className="text-sm italic font-light">
													- (optional)
												</span>
											</FormLabel>
											<FormControl>
												<Input
													{...field}
													type="number"
													min={0}
													step="1"
													placeholder="e.g., 100000"
													className="bg-input appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
												/>
											</FormControl>
											<FormDescription>
												Maximum discount amount in rupiah (e.g., 100000 for
												Rp100.000)
											</FormDescription>
											<FormMessage />
										</FormItem>
									)}
								/>
							</>
						)}

						<FormField
							control={form.control}
							name="minOrderAmount"
							render={({ field }) => (
								<FormItem>
									<FormLabel className="capitalize">
										Min Order Amount{" "}
										<span className="text-sm italic font-light">
											- (optional)
										</span>
									</FormLabel>
									<FormControl>
										<Input
											{...field}
											type="number"
											min={0}
											step="1"
											placeholder="e.g., 500000"
											className="bg-input appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
										/>
									</FormControl>
									<FormDescription>
										Minimum order amount in rupiah (e.g., 500000 for Rp500.000)
									</FormDescription>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="quota"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Quota</FormLabel>
									<FormControl>
										<Input
											{...field}
											type="number"
											min={0}
											step="1"
											placeholder="Enter quota..."
											className="bg-input appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="isActive"
							render={({ field }) => (
								<FormItem className="flex flex-col gap-2">
									<div className="flex items-center justify-between rounded-md border px-3 py-2">
										<div>
											<FormLabel className="text-base">Status</FormLabel>
											<p className="text-sm text-muted-foreground">
												Toggle to activate or pause the voucher.
											</p>
										</div>

										<FormControl>
											<Switch
												checked={field.value}
												onCheckedChange={field.onChange}
											/>
										</FormControl>
									</div>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="adminOnly"
							render={({ field }) => (
								<FormItem className="flex flex-col gap-2">
									<div className="flex items-center justify-between rounded-md border px-3 py-2">
										<div>
											<FormLabel className="text-base">Staff only</FormLabel>
											<p className="text-sm text-muted-foreground">
												Restrict redemption to staff accounts (admin or staff
												role) instead of any customer.
											</p>
										</div>

										<FormControl>
											<Switch
												checked={field.value}
												onCheckedChange={field.onChange}
											/>
										</FormControl>
									</div>
									<FormMessage />
								</FormItem>
							)}
						/>

						{bonusRoEditable && (
							<div className="space-y-3 md:col-span-2">
								<FormField
									control={form.control}
									name="bonusRo"
									render={({ field }) => (
										<FormItem className="flex flex-col gap-2">
											<div className="flex items-center justify-between rounded-md border px-3 py-2">
												<div>
													<FormLabel className="text-base">Bonus RO</FormLabel>
													<p className="text-sm text-muted-foreground">
														{voucher?.program === "BONUS_RO" && field.value
															? "Turn off to unlink the therapist."
															: "Therapist's personal repeat-order voucher."}
													</p>
												</div>

												<FormControl>
													<Switch
														checked={field.value}
														onCheckedChange={(checked) => {
															field.onChange(checked);
															if (!checked) {
																form.setValue("therapistId", "");
																setOwnerName("");
															}
														}}
													/>
												</FormControl>
											</div>
										</FormItem>
									)}
								/>

								{bonusRo && !isBonusRoVoucher && (
									<p
										role="note"
										className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800"
									>
										<TriangleAlert className="mt-0.5 size-4 shrink-0" />
										Voucher Bonus RO memotong per paket sesuai Potongan pasien
										di tab Rules halaman Bonus RO. Tipe dan nilai diskon di atas
										tidak dipakai.
									</p>
								)}

								{bonusRo && (
									<FormField
										control={form.control}
										name="therapistId"
										render={({ field }) => (
											<FormItem>
												<FormLabel>Owner Therapist</FormLabel>
												{field.value ? (
													<div className="flex items-center justify-between rounded-md border px-3 py-2">
														<span className="font-medium">{ownerName}</span>
														<Button
															type="button"
															variant="ghost"
															size="sm"
															onClick={() => {
																field.onChange("");
																setOwnerName("");
															}}
														>
															Change
														</Button>
													</div>
												) : (
													<AsyncSearchCombobox<TherapistOption>
														fetchUrl={`${globalProps.adminPortal.router.adminPortal.vouchers.index}/therapist-options`}
														getKey={(therapist) => therapist.id}
														renderItem={(therapist) => (
															<div className="flex w-full items-center justify-between gap-2">
																<span>
																	{therapist.name}{" "}
																	<span className="text-xs text-muted-foreground">
																		&bull; {therapist.registrationNumber}
																	</span>
																</span>
																{therapist.employmentStatus !== "ACTIVE" && (
																	<Badge
																		variant="outline"
																		className="text-[10px] uppercase"
																	>
																		{therapist.employmentStatus}
																	</Badge>
																)}
															</div>
														)}
														placeholder="Search name or registration number..."
														emptyText="Type at least 2 characters to search."
														onSelect={(therapist) => {
															field.onChange(therapist.id);
															setOwnerName(therapist.name);
														}}
													/>
												)}
												<FormMessage />
											</FormItem>
										)}
									/>
								)}
							</div>
						)}
					</div>

					<FormField
						control={form.control}
						name="description"
						render={({ field }) => (
							<FormItem>
								<FormLabel className="capitalize">
									Description{" "}
									<span className="text-sm italic font-light">
										- (optional)
									</span>
								</FormLabel>
								<FormControl>
									<Textarea
										{...field}
										placeholder="Enter description..."
										className="bg-input"
										rows={4}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<div className="grid gap-4 md:grid-cols-2">
						<FormField
							control={form.control}
							name="validFrom"
							render={({ field }) => (
								<FormItem>
									<FormLabel className="capitalize">
										Valid From{" "}
										<span className="text-sm italic font-light">
											- (optional)
										</span>
									</FormLabel>
									<FormControl>
										<Input
											{...field}
											type="datetime-local"
											className="flow-root w-full bg-input"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="validUntil"
							render={({ field }) => (
								<FormItem>
									<FormLabel className="capitalize">
										Valid Until{" "}
										<span className="text-sm italic font-light">
											- (optional)
										</span>
									</FormLabel>
									<FormControl>
										<Input
											{...field}
											type="datetime-local"
											className="flow-root w-full bg-input"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
				</div>

				<ResponsiveDialogButton {...buttonProps} />
			</form>
		</Form>
	);
}

function parseOptionalNumber(value?: string | null) {
	if (!value) return null;
	const parsed = Number(value);
	return Number.isNaN(parsed) ? null : parsed;
}

function formatNumericValue(value: number | string | null | undefined): string {
	if (value == null || value === "") return "";
	const str = String(value);
	return str.endsWith(".0") ? str.slice(0, -2) : str;
}

function mapVoucherToFormValues(voucher: Voucher): VoucherFormValues {
	return {
		code: voucher.code ?? "",
		name: voucher.name ?? "",
		description: voucher.description ?? "",
		discountType:
			voucher.discountType?.toUpperCase() as VoucherFormValues["discountType"],
		discountValue: formatNumericValue(voucher.discountValue),
		maxDiscountAmount: formatNumericValue(voucher.maxDiscountAmount),
		minOrderAmount: formatNumericValue(voucher.minOrderAmount),
		quota: String(voucher.quota ?? ""),
		validFrom: voucher.validFrom
			? formatDateTimeLocal(new Date(voucher.validFrom))
			: "",
		validUntil: voucher.validUntil
			? formatDateTimeLocal(new Date(voucher.validUntil))
			: "",
		isActive: Boolean(voucher.isActive),
		adminOnly: Boolean(voucher.adminOnly),
		packageIds: (voucher.packages ?? []).map((pkg) => String(pkg.id)),
		bonusRo: Boolean(ownerOf(voucher)),
		therapistId: ownerOf(voucher)?.id ?? "",
	};
}

// A failed link's pick wins over the saved owner, so the retry starts from it. A therapist
// without the Bonus RO program is not an owner.
function ownerOf(voucher: Voucher) {
	if (voucher.pendingOwner) return voucher.pendingOwner;
	return voucher.program === "BONUS_RO" ? (voucher.therapist ?? null) : null;
}

function formatDateTimeLocal(date: Date) {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	const hours = String(date.getHours()).padStart(2, "0");
	const minutes = String(date.getMinutes()).padStart(2, "0");
	return `${year}-${month}-${day}T${hours}:${minutes}`;
}
